import Script from 'next/script';
import {useCallback, useEffect, useRef, useState} from 'react';

import {useOnScreen} from '@utils/hooks/useOnScreen';
import {useWindowFocusListener} from '@utils/hooks/useWindowFocusListener';

import {AD_AUTOREFRESH_INTERVAL_MS, BannerCodes} from '../consts';
import {useAdAnalytics} from '../hooks/useAdAnalytics';
import {AdResponseType, BannerProps} from '../types';
import {bindClickLogger, getAdvManager, log} from '../utils';
import {getBannerContainerId} from '../utils/getBannerContainerId';
import {AdFoxBanner} from './AdFox/AdFoxBanner';

type ContainerProps = {
  id: string;
  className: string;
  onClick: () => void;
  onVisibilityChange: (visible: boolean) => void;
};

const observerOptions: IntersectionObserverInit = {
  threshold: 0.5,
};

const BannerContainer = ({
  id,
  className,
  onClick,
  onVisibilityChange,
}: ContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useOnScreen(containerRef, false, observerOptions);

  useEffect(() => {
    onVisibilityChange(inView);
  }, [inView, onVisibilityChange]);

  useEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return;
    }

    return bindClickLogger(element, () => {
      log('click');
      onClick();
    });
  }, [onClick]);

  return <div ref={containerRef} id={id} className={className} />;
};

const YANBanner = ({
  bannerContainerClassName,
  blockId,
  adType,
  onError,
  onNoAds,
  position,
  placement,
  emitYmEvent,
  logTmrEvent,
}: BannerProps) => {
  const containerId = getBannerContainerId(placement, position);
  const [renderContainer, setRenderContainer] = useState(true);
  const adLog = useAdAnalytics({
    platform: 'yandex',
    adType,
    emitYmEvent,
    logTmrEvent,
  });
  const shouldRenderElements = renderContainer && !!blockId;
  const adWasRenderedAtLeastOnceRef = useRef(false);
  const [adRendered, setAdRendered] = useState(false);
  const bannerVisibleRef = useRef(false);
  const [bannerVisible, setBannerVisible] = useState(false);
  const showWasLoggedRef = useRef(false);
  const lastRefreshTs = useRef<number | null>(null);

  const onScriptError = useCallback(() => {
    adLog.blocked(blockId, position);
    onError && onError();
  }, [adLog, blockId, onError]);

  const initAd = useCallback(
    (firstRequest: boolean) => {
      if (!blockId) {
        return;
      }
      if (
        lastRefreshTs.current &&
        Date.now() - lastRefreshTs.current < AD_AUTOREFRESH_INTERVAL_MS
      ) {
        log(`${containerId} skipping ad refresh since it was recently`);
        return;
      }

      lastRefreshTs.current = Date.now();

      log(`initializing ad ${containerId}`);

      window.yaContextCb = window.yaContextCb || [];

      window.yaContextCb.push(() => {
        if (firstRequest) {
          adLog.requested(blockId, position);
        }

        getAdvManager().render(
          {
            blockId,
            renderTo: containerId,
            onRender: (data) => {
              log('rendered', data);
              setAdRendered(true);
              setRenderContainer(true);
              if (!adWasRenderedAtLeastOnceRef.current) {
                adLog.gotResponse(blockId, position, {
                  AdResponse: AdResponseType.HAS_ADS,
                });
                adWasRenderedAtLeastOnceRef.current = true;
                adLog.rendered(blockId, position);

                if (bannerVisibleRef.current && !showWasLoggedRef.current) {
                  showWasLoggedRef.current = true;
                  adLog.shown(blockId, position);
                }
              }
            },
            onError: (data) => {
              log('error', data);
              setRenderContainer(false);
              onError && onError();
            },
            //Вероятно нигде не юзается
            onClose: () => {
              log('closed');
              adLog.closed(blockId, position);
            },
          },
          () => {
            log('fallback called');
            adLog.gotResponse(blockId, position, {
              AdResponse: AdResponseType.NO_ADS,
            });
            adLog.nothingToShow(blockId, position);
            setRenderContainer(false);
            onNoAds && onNoAds();
          },
        );
      });
    },
    [adLog, blockId, containerId, onError, onNoAds],
  );

  useEffect(() => {
    initAd(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const windowHasFocus = useWindowFocusListener(
    useCallback(() => {
      if (adWasRenderedAtLeastOnceRef.current && bannerVisibleRef.current) {
        log(`${containerId} refreshing ad on window focus`);
        initAd(false);
      }
    }, [containerId, initAd]),
  );

  useEffect(() => {
    if (!adRendered || !bannerVisible || !windowHasFocus) {
      return;
    }

    log(`${containerId} scheduling refreshing timer`);
    const intervalHandle = setInterval(() => {
      log(`${containerId} refreshing ad on timer`);
      initAd(false);
    }, AD_AUTOREFRESH_INTERVAL_MS);
    return () => {
      clearInterval(intervalHandle);
    };
  }, [adRendered, bannerVisible, windowHasFocus, containerId, initAd]);

  const onClick = useCallback(() => {
    adLog.clicked(blockId, position);
  }, [adLog, blockId]);

  const onVisibilityChange = useCallback(
    (visible: boolean) => {
      setBannerVisible(visible);
      bannerVisibleRef.current = visible;
      if (
        adWasRenderedAtLeastOnceRef.current &&
        visible &&
        !showWasLoggedRef.current
      ) {
        showWasLoggedRef.current = true;
        adLog.shown(blockId, position);
      }
    },
    [adLog, blockId],
  );

  if (!shouldRenderElements) {
    return null;
  }

  return (
    <>
      <Script
        id="yandex-ads"
        src="https://yandex.ru/ads/system/context.js"
        async
        onError={onScriptError}
      />
      <BannerContainer
        data-testid={containerId}
        id={containerId}
        className={bannerContainerClassName}
        onClick={onClick}
        onVisibilityChange={onVisibilityChange}
      />
    </>
  );
};

/** Используется для рекламы на местах приложений в списках, карточках со скринами,
 * и как кастомная реклама по скроллу для YaMobileBanner
 */
export const YandexBannerAd = (props: BannerProps) => {
  const code = BannerCodes[props.placement];
  if (props.adFoxConfig.enabled) {
    if (code) {
      return <AdFoxBanner {...props} />;
    }
    console.warn(
      `No AdFox config for placement ${props.placement}, falling back to YAN`,
    );
  }
  return <YANBanner {...props} />;
};
